/**
 * Derive discovery search queries from the loaded vehicle config's applicable
 * cards (RFC-0002 C1 / §5A). Takes the top-2 most-frequent applicable
 * `regulatoryDomain` values × the target markets → ≤2 focused query strings.
 * Pure + deterministic; the query is a search string, never a legal claim.
 */

import type { ApplicabilityCategory } from "@regpulse/contracts";

export interface QueryDerivCard {
  regulatoryDomain: string;
  applicabilityCategory: ApplicabilityCategory;
}

export function deriveDiscoveryQueries(cards: QueryDerivCard[], markets: string[]): string[] {
  const counts = new Map<string, number>();
  for (const c of cards) {
    if (c.applicabilityCategory !== "applicable") continue;
    counts.set(c.regulatoryDomain, (counts.get(c.regulatoryDomain) ?? 0) + 1);
  }
  const topDomains = [...counts.entries()]
    // Most frequent first; tie-break alphabetically so the output is fully
    // deterministic regardless of card ordering.
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 2)
    .map(([domain]) => domain);
  if (topDomains.length === 0) return [];
  const marketLabel = markets.length > 0 ? markets.join("+") : "EU";
  return topDomains.map((domain) => `${marketLabel} automotive ${domain} regulatory update`);
}
